import type { ExportCustomerContractsRow } from '@ebalance/proto/bffuser/v1/customer_contract_csv_model_pb'
import { ContractStage } from '@ebalance/proto/bffuser/v1/customer_model_pb'
import { VoltageClass } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'

import { convertAreaLabel } from '~/models/area'
import { type CivilDate, formatCivilDateSlash } from '~/models/civilDate'
import {
  type CustomerContractCsvColumn,
  serializeCustomerContractCsvExportRows,
} from '~/models/customerContractCsvHeader'

/**
 * 顧客契約 CSV エクスポートの行変換・CSV 組み立て (uploads/common.md §6 AC-DUC-028/029、
 * 列定義は docs/specs/admin/data/uploads/customer-contract.md §2)。
 *
 * 出力列はアップロード CSV と同一の 17 列 (地点Id, 需要家Id, 区分, エリア, 需要家名, 供給地点特定番号,
 * 電圧階級, 契約電力, 供給開始日, 供給終了日, 事業者コード, 自家発 3 列, 契約者Id, 契約者名, 契約電力の変更)。
 * 契約電力の変更は「適用開始日:契約電力」を ; で並べた 1 セル (最初の期間の後のもの。例 2027-04-01:400;2028-04-01:350)。
 * 区分・電圧階級はアップロード側パーサが受理する日本語ラベルの固定表記で書く (表示言語に依らず
 * ダウンロード → 編集 → 再アップロードが往復する。エリアは `convertAreaLabel`、取込側は日英どちらも受理)。
 * 並び順は backend が決める (エリア → 番号〈空は最後〉→ 供給開始日 → 地点Id)。
 */

/** CSV 1 行分のデータ (列名 key の全列)。 */
export type CustomerContractCsvExportRow = Record<
  CustomerContractCsvColumn,
  string
>

const STAGE_LABEL: Partial<Record<ContractStage, string>> = {
  [ContractStage.CONTRACTED]: '契約',
  [ContractStage.PROSPECT]: '見込み',
  [ContractStage.LOST]: '失注',
}

const VOLTAGE_LABEL: Partial<Record<VoltageClass, string>> = {
  [VoltageClass.EXTRA_HIGH]: '特別高圧',
  [VoltageClass.HIGH]: '高圧',
  [VoltageClass.LOW]: '低圧',
}

/** 契約電力の期間を 適用開始日:契約電力 を ; で並べた 1 セルにする (取込側が読む書き方)。 */
const formatKwPeriodsCell = (
  periods: ReadonlyArray<{
    startDate?: CivilDate
    contractKw?: { value: string }
  }>,
): string =>
  periods
    .map(
      (p) =>
        `${formatCivilDateSlash(p.startDate).replaceAll('/', '-')}:${p.contractKw?.value ?? ''}`,
    )
    .join(';')

export const toCustomerContractCsvExportRow = (
  row: ExportCustomerContractsRow,
): CustomerContractCsvExportRow => {
  const generation = row.privateGeneration
  const hasGeneration = generation?.has === true
  return {
    地点Id: row.customerSupplyPointId,
    需要家Id: row.customerId,
    区分: STAGE_LABEL[row.contractStage] ?? '',
    エリア: convertAreaLabel(row.areaCode),
    需要家名: row.customerName,
    供給地点特定番号: row.supplyPointSpecificNumber ?? '',
    電圧階級: VOLTAGE_LABEL[row.voltageClass] ?? '',
    '契約電力 (kW)': row.contractKw?.value ?? '',
    供給開始日: formatCivilDateSlash(row.supplyStartDate),
    供給終了日: formatCivilDateSlash(row.supplyEndDate),
    事業者コード: row.retailerBusinessCode ?? '',
    自家発有無: hasGeneration ? '1' : '0',
    '自家発容量 (kW)': hasGeneration
      ? (generation.capacityKw?.value ?? '')
      : '',
    '逆調契約 (kW)': hasGeneration
      ? (generation.reversePowerFlowContractKw?.value ?? '')
      : '',
    契約者Id: row.contractorId,
    契約者名: row.contractorName,
    契約電力の変更: formatKwPeriodsCell(row.kwPeriods),
  }
}

/** 応答全行を CSV 行へ変換する (順序は backend が決めた並びを保つ)。 */
export const toCustomerContractCsvExportRows = (
  rows: readonly ExportCustomerContractsRow[],
): CustomerContractCsvExportRow[] => rows.map(toCustomerContractCsvExportRow)

/** エクスポート行から CSV 本文を組み立てる (列順・エスケープ・改行は serializer の単一 SoT)。BOM は保存時に付く。 */
export const buildCustomerContractCsvExport = (
  rows: readonly CustomerContractCsvExportRow[],
): string => serializeCustomerContractCsvExportRows(rows)
